import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Save, Copy } from 'lucide-react';
import { toast } from 'sonner';

export default function OrgSettings() {
  const { api } = useAuth();
  const [org, setOrg] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/orgs/me');
      setOrg(data.organization);
      setForm({
        name: data.organization.name || '',
        org_type: data.organization.org_type || 'fpo',
        contact_phone: data.organization.contact_phone || '',
        contact_email: data.organization.contact_email || '',
        address: data.organization.address || '',
        gstin: data.organization.gstin || '',
      });
    } catch (err) {
      toast.error('Failed to load');
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/orgs/me', form);
      toast.success('Saved');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(org?.code || '');
    toast.success('Org code copied');
  };

  if (!org) return <div className="bg-[#F6F8FB] rounded-2xl border border-[#E4E8EF] p-4 h-48 animate-pulse" />;

  return (
    <form onSubmit={handleSave} className="space-y-4" data-testid="org-settings">
      <div className="bg-[#F6F8FB] rounded-2xl border border-[#E4E8EF] p-4">
        <p className="text-xs text-[#5B6675] mb-1">Share this code with farmers to let them join</p>
        <button
          type="button"
          data-testid="org-settings-copy-code"
          onClick={copyCode}
          className="text-lg font-mono font-bold text-[#0B4F3F] bg-[#EFF2F7] rounded-lg px-4 py-2 flex items-center gap-2"
        >
          {org.code} <Copy className="w-4 h-4" />
        </button>
      </div>

      <div className="bg-[#F6F8FB] rounded-2xl border border-[#E4E8EF] p-4 space-y-3">
        <h3 className="text-sm font-semibold text-[#0B1620] font-['Bricolage_Grotesque']">Organization profile</h3>
        {[
          { key: 'name', label: 'Organization name' },
          { key: 'contact_phone', label: 'Contact phone' },
          { key: 'contact_email', label: 'Contact email' },
          { key: 'address', label: 'Address' },
          { key: 'gstin', label: 'GSTIN' },
        ].map(({ key, label }) => (
          <div key={key}>
            <Label className="text-xs text-[#5B6675]">{label}</Label>
            <Input
              data-testid={`org-settings-${key}`}
              value={form[key] || ''}
              onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              className="h-10 bg-[#F6F8FB] border-[#E4E8EF] rounded-lg text-sm mt-1"
            />
          </div>
        ))}
        <Button
          data-testid="org-settings-save-btn"
          type="submit"
          disabled={saving}
          className="w-full h-10 bg-[#0B4F3F] text-[#F6F8FB] rounded-full"
        >
          <Save className="w-4 h-4 mr-1" />{saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </form>
  );
}
